import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';
import './Login.css';

function Login() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      if (data.session) {
        // Session is automatically saved by Supabase to localStorage
        // Verify session is saved before redirecting
        const { data: { session: savedSession } } = await supabase.auth.getSession();
        
        if (savedSession) {
          // Session successfully saved, redirect to dashboard
          navigate('/dashboard');
        } else {
          setError('Failed to save session. Please try again.');
          setLoading(false);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      if (data.user) {
        // Check if session was created (email confirmation not required)
        if (data.session) {
          // Session is automatically saved by Supabase to localStorage
          // Verify session is saved before redirecting
          const { data: { session: savedSession } } = await supabase.auth.getSession();
          
          if (savedSession) {
            // Session successfully saved, redirect to dashboard
            navigate('/dashboard');
          } else {
            setError('Failed to save session. Please try again.');
            setLoading(false);
          }
        } else {
          // Email confirmation required
          setSuccess('Account created successfully! Please check your email to confirm your account.');
          setIsSignUp(false);
          setPassword('');
          setLoading(false);
        }
      }
    } catch (err) {
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!email) {
      setError('Please enter your email address');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setError(error.message);
        setLoading(false);
        return;
      }

      setSuccess('Password reset email sent! Please check your inbox.');
      setLoading(false);
    } catch (err) {
      setError('An unexpected error occurred');
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-actions">
        <button
          type="button"
          className="link-button"
          onClick={() => navigate('/')}
        >
          ← Back to home
        </button>
      </div>
      <div className="login-box">
        <h2>
          {showForgotPassword ? 'Reset Password' : isSignUp ? 'Sign Up' : 'Login'}
        </h2>
        
        {showForgotPassword ? (
          <form onSubmit={handleForgotPassword}>
            <div className="form-group">
              <label htmlFor="reset-email">Email</label>
              <input
                type="email"
                id="reset-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="Enter your email"
                autoComplete="email"
              />
            </div>
            <p className="forgot-password-info">
              Enter your email address and we'll send you a link to reset your password.
            </p>
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}
            <button type="submit" disabled={loading} className="login-button">
              {loading ? 'Sending...' : 'Send Reset Link'}
            </button>
            <div className="form-footer">
              <button 
                type="button" 
                className="link-button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError('');
                  setSuccess('');
                }}
              >
                ← Back to Login
              </button>
            </div>
          </form>
        ) : (
          <>
            <form onSubmit={isSignUp ? handleSignUp : handleLogin}>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="user@example.com"
                  autoComplete="email"
                />
              </div>
              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  minLength={6}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                />
                {!isSignUp && (
                  <div className="forgot-password-link">
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setError('');
                        setSuccess('');
                        setPassword('');
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                )}
              </div>
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
              <button type="submit" disabled={loading} className="login-button">
                {loading 
                  ? (isSignUp ? 'Creating account...' : 'Logging in...') 
                  : (isSignUp ? 'Sign Up' : 'Login')
                }
              </button>
            </form>
            <div className="form-footer">
              {isSignUp ? (
                <p>
                  Already have an account?{' '}
                  <button 
                    type="button" 
                    className="link-button"
                    onClick={() => {
                      setIsSignUp(false);
                      setError('');
                      setSuccess('');
                    }}
                  >
                    Login
                  </button>
                </p>
              ) : (
                <p>
                  Don't have an account?{' '}
                  <button 
                    type="button" 
                    className="link-button"
                    onClick={() => {
                      setIsSignUp(true);
                      setError('');
                      setSuccess('');
                    }}
                  >
                    Sign Up
                  </button>
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Login;
